import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import "./OnboardingScreen.scss";
import timeIconLast from "../../../assets/timeIcon.png";
import { useNavigate } from "react-router-dom";

const OnboardingScreen = () => {
  const [currentScreen, setCurrentScreen] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSports, setSelectedSports] = useState([]);
  const [selectedWorkProfile, setSelectedWorkProfile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [memberDetails, setMemberDetails] = useState(null);
  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Function to fetch member details
  const fetchMemberDetails = async () => {
    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/get_member_details`;
    const accessToken = window.localStorage.getItem("access_token");

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("API Response:", data);
      setMemberDetails(data); // Set entire data object
      setHasFetchedDetails(true);
    } catch (error) {
      console.error("Fetch Member Details Error:", error.message);
      toast.error("Failed to fetch member details. You can still proceed.");
      setMemberDetails({});
      setHasFetchedDetails(true);
    }
  };

  // Fetch member details on component mount
  useEffect(() => {
    fetchMemberDetails();
  }, []);

  // Determine which screens to skip based on memberDetails
  const shouldSkipSportsScreen =
    memberDetails?.interested_sports &&
    memberDetails.interested_sports.length > 0;
  const shouldSkipWorkProfileScreen =
    memberDetails?.member_work_profile &&
    memberDetails.member_work_profile !== null &&
    memberDetails.member_work_profile.trim() !== "";
  const shouldSkipImageScreen =
    memberDetails?.member_profile_pic_url &&
    memberDetails.member_profile_pic_url.trim() !== "";

  // Set the initial screen based on memberDetails
  useEffect(() => {
    if (!hasFetchedDetails) return;
    let initialScreen = 1;
    if (shouldSkipSportsScreen) {
      initialScreen = shouldSkipWorkProfileScreen
        ? shouldSkipImageScreen
          ? 5
          : 4
        : 3;
    }
    setCurrentScreen(initialScreen);
  }, [
    hasFetchedDetails,
    shouldSkipSportsScreen,
    shouldSkipWorkProfileScreen,
    shouldSkipImageScreen,
  ]);

  // API function to update member interested sports
  const updateMemberInterestedSports = async (sports) => {
    try {
      const accessToken = window.localStorage.getItem("access_token");
      const url =
        "https://ballersapi.ballerslife.co/api/v1/memberships/member_interested_sports";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sports),
      });

      if (!response.ok) {
        throw new Error("Failed to update member interested sports");
      }

      const data = await response.json();
      if (response.status === 200) {
        // toast.success(data.message);
        await fetchMemberDetails(); // Refresh member details after successful update
      } else {
        toast.error(data.message);
      }
      return true;
    } catch (error) {
      toast.error("Failed to update sports preferences.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to update member work profile
  const updateMemberWorkProfile = async (workProfile) => {
    try {
      const accessToken = window.localStorage.getItem("access_token");
      const url =
        "https://ballersapi.ballerslife.co/api/v1/memberships/member_work_profile";
      const response = await fetch(
        `${url}?work_profile=${encodeURIComponent(workProfile)}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: "",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update member work profile");
      }

      const data = await response.json();
      if (response.status === 200) {
        // toast.success(data.message);
        await fetchMemberDetails(); // Refresh member details after successful update
      } else {
        toast.error(data.message);
      }
      return true;
    } catch (error) {
      toast.error("Failed to update work profile.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to upload profile picture
  const handleUploadImage = async () => {
    if (!selectedImage || !selectedImage.file) {
      toast.error("Please select an image to upload.");
      return false;
    }

    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/member_profile_pic_update`;
    const accessToken = window.localStorage.getItem("access_token");

    const formData = new FormData();
    formData.append("member_profile_pic", selectedImage.file);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (response.status === 200) {
        // toast.success(data.message);
        await fetchMemberDetails(); // Refresh member details after successful upload
        return true;
      } else {
        toast.error(data.detail || "Failed to update profile picture.");
        return false;
      }
    } catch (err) {
      toast.error("An error occurred while updating the profile picture.");
      return false;
    }
  };

  const handleNextScreen = async () => {
    // Validation for screen 2 (sports selection)
    if (currentScreen === 2) {
      if (selectedSports.length === 0) {
        toast.error("Please select at least one sport to proceed.");
        return;
      }
      setIsLoading(true);
      const success = await updateMemberInterestedSports(selectedSports);
      if (!success) return;
    }

    // Validation for screen 3 (work profile selection)
    if (currentScreen === 3) {
      if (!selectedWorkProfile) {
        toast.error("Please select a work profile to proceed.");
        return;
      }
      setIsLoading(true);
      const success = await updateMemberWorkProfile(selectedWorkProfile);
      if (!success) return;
    }

    // Handle image upload for screen 4
    if (currentScreen === 4) {
      if (selectedImage) {
        setIsLoading(true);
        const success = await handleUploadImage();
        setIsLoading(false);
        if (!success) return;
      }
    }

    // Determine the next screen, skipping based on memberDetails
    let nextScreen = currentScreen + 1;
    if (nextScreen === 2 && shouldSkipSportsScreen) {
      nextScreen++;
    }
    if (nextScreen === 3 && shouldSkipWorkProfileScreen) {
      nextScreen++;
    }
    if (nextScreen === 4 && shouldSkipImageScreen) {
      nextScreen++;
    }

    if (nextScreen <= 5) {
      setCurrentScreen(nextScreen);
    }
  };

  const handlePreviousScreen = () => {
    let prevScreen = currentScreen - 1;
    // Prevent navigating back to skipped screens
    if (prevScreen === 4 && shouldSkipImageScreen) {
      prevScreen--;
    }
    if (prevScreen === 3 && shouldSkipWorkProfileScreen) {
      prevScreen--;
    }
    if (prevScreen === 2 && shouldSkipSportsScreen) {
      prevScreen--;
    }
    if (prevScreen >= 1) {
      setCurrentScreen(prevScreen);
    }
  };

  const handleSkip = () => {
    // Allow skipping on screens 4 and 5, but respect memberDetails skips
    let nextScreen = currentScreen + 1;
    if (nextScreen === 4 && shouldSkipImageScreen) {
      nextScreen++;
    }
    if (nextScreen <= 5) {
      setCurrentScreen(nextScreen);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage({ file, url: imageUrl });
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSportSelection = (sport) => {
    setSelectedSports((prev) =>
      prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]
    );
  };

  const handleWorkProfileSelection = (role) => {
    setSelectedWorkProfile(role);
  };

  const handleSkipToDashboard = () => {
    navigate("/dashboard/booking");
  };

  return (
    <div className="onboarding-welcome-container">
      {currentScreen === 1 && (
        <>
          <div className="onboarding-logo-placeholder"></div>
          <h1 className="onboarding-title-welcome">Welcome To Ballers!</h1>
          <p className="onboarding-subtitle">
            Your ultimate game plan starts here!
          </p>
          <p className="onboarding-message">
            Let's get to know you better so we can kick off your journey!
          </p>
          <p className="onboarding-prompt">Ready to level up?</p>
          <button
            className="onboarding-button"
            onClick={handleNextScreen}
            disabled={isLoading || !hasFetchedDetails}
          >
            {isLoading ? "Loading..." : "Let's Go!"}
          </button>
        </>
      )}

      {currentScreen === 2 && (
        <>
          <div className="onboarding-progress-indicator">
            {[2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`progress-step ${
                  step <= currentScreen ? "active" : ""
                }`}
              ></div>
            ))}
          </div>
          <div className="onboarding-header">
            <span
              className="onboarding-back-arrow"
              onClick={handlePreviousScreen}
            >
              ←
            </span>
          </div>
          <h1 className="onboarding-title">What's Your Sport of Choice?</h1>
          <p className="onboarding-subtitle">
            (Choose all you want, no limitations here!)
          </p>
          <div className="onboarding-options">
            {[
              "Basketball",
              "Volleyball",
              "Pickleball",
              "Tennis",
              "Padel",
              "Cricket",
              "Badminton",
            ].map((sport) => (
              <label key={sport} className="onboarding-option">
                <input
                  type="checkbox"
                  className="onboarding-checkbox"
                  checked={selectedSports.includes(sport)}
                  onChange={() => handleSportSelection(sport)}
                />
                <span className="onboarding-checkbox-custom"></span>
                <span className="onboarding-option-label">{sport}</span>
              </label>
            ))}
          </div>
          <button
            className="onboarding-button"
            onClick={handleNextScreen}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Keep Rolling"}
          </button>
        </>
      )}

      {currentScreen === 3 && (
        <>
          <div className="onboarding-progress-indicator">
            {[2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`progress-step ${
                  step <= currentScreen ? "active" : ""
                }`}
              ></div>
            ))}
          </div>
          <div className="onboarding-header">
            <span
              className="onboarding-back-arrow"
              onClick={handlePreviousScreen}
            >
              ←
            </span>
          </div>
          <h1 className="onboarding-title">What's Your Game Off the Field?</h1>
          <p className="onboarding-subtitle">
            (Choose the role that best describes you)
          </p>
          <div className="onboarding-options">
            {[
              "Business Owner",
              "Corporate Employee",
              "Freelancer",
              "Sports Professional",
              "Investor",
              "Student",
            ].map((role) => (
              <label key={role} className="onboarding-option">
                <input
                  type="radio"
                  name="role"
                  className="onboarding-radio"
                  checked={selectedWorkProfile === role}
                  onChange={() => handleWorkProfileSelection(role)}
                />
                <span className="onboarding-radio-custom"></span>
                <span className="onboarding-option-label">{role}</span>
              </label>
            ))}
          </div>
          <button
            className="onboarding-button"
            onClick={handleNextScreen}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Keep Rolling"}
          </button>
        </>
      )}

      {currentScreen === 4 && (
        <>
          <div className="onboarding-progress-indicator">
            {[2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`progress-step ${
                  step <= currentScreen ? "active" : ""
                }`}
              ></div>
            ))}
          </div>
          <div className="onboarding-header">
            <span
              className="onboarding-back-arrow"
              onClick={handlePreviousScreen}
            >
              ←
            </span>
            <span className="onboarding-skip" onClick={handleSkip}>
              Skip For Now
            </span>
          </div>
          <h1 className="onboarding-title">Show Us Your Game Face!</h1>
          <p className="onboarding-subtitle">
            Help us know who's bringing the heat to the courts!
          </p>
          <div
            className="onboarding-image-placeholder"
            style={
              selectedImage
                ? {
                    backgroundImage: `url(${selectedImage.url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : {}
            }
          >
            <span
              className="onboarding-upload-icon"
              onClick={handleUploadClick}
            >
              ↑
            </span>
            <input
              type="file"
              accept="image/*"
              className="onboarding-upload-input"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
          </div>
          <button
            className="onboarding-button"
            onClick={handleNextScreen}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Lock In"}
          </button>
        </>
      )}

      {currentScreen === 5 && (
        <>
          <div className="onboarding-progress-indicator">
            {[2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`progress-step ${
                  step <= currentScreen ? "active" : ""
                }`}
              ></div>
            ))}
          </div>
          <div className="onboarding-header">
            <span
              className="onboarding-back-arrow"
              onClick={handlePreviousScreen}
            >
              ←
            </span>
            <span
              className="onboarding-skip"
              onClick={() => {
                handleSkipToDashboard();
              }}
            >
              <u>Skip</u>
            </span>
          </div>
          <h1 className="onboarding-title-welcome">You're All Set To Play!</h1>
          <p className="onboarding-subtitle">
            Time to lock in your baller pass and bring the heat!
          </p>
          <div className="onboarding-benefits">
            <p>
              <span className="benefit-tick">✔</span> 1000+ Facilities
            </p>
            <p>
              <span className="benefit-tick">✔</span> Zero Booking Fee
            </p>
            <p>
              <span className="benefit-tick">✔</span> Member Only Events
            </p>
            <p>
              <span className="benefit-tick">✔</span> Partner Perks
            </p>
            <p>
              <span className="benefit-tick">✔</span> Lifetime Membership
            </p>
            <p>
              <span className="benefit-tick">✔</span> Tournaments
            </p>
          </div>
          <div className="timeIcon">
            <img src={timeIconLast} alt="Time Icon" />
          </div>
          <p className="onboarding-note">
            Only 500 Spots Per City, Lock Yours In Before It's Gone!
          </p>
          <button
            className="onboarding-button"
            onClick={() => {
              handleSkipToDashboard();
            }}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Get Membership"}
          </button>
        </>
      )}
    </div>
  );
};

export default OnboardingScreen;
